import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { ArrowLeft, Users, Package as PackageIcon, Check, Truck, Flame, Award } from "lucide-react";
import ComboSuggestions from "@/components/ComboSuggestions";
import SafeImage from "@/components/SafeImage";

export default function PackageDetailPage() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [combos, setCombos] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    setCombos([]);
    api.get(`/packages/${slug}`).then((r) => {
      setPkg(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const onCombosChange = useCallback((list) => setCombos(list), []);

  const combosTotal = combos.reduce((s, p) => s + (p.variants?.[0]?.price ?? p.price), 0);
  const grandTotal = (pkg?.price || 0) + combosTotal;

  const handleAddToCart = () => {
    if (!pkg) return;
    addItem({ type: "package", id: pkg.slug, name: pkg.name, price: pkg.price, image: pkg.image, unit: pkg.serves });
    combos.forEach((p) => {
      const v = p.variants?.[0];
      addItem({
        type: "product",
        id: p.id,
        variant_id: v?.id || null,
        name: v && (p.variants?.length || 0) > 1 ? `${p.name} — ${v.label}` : p.name,
        price: v?.price ?? p.price,
        image: p.image,
        unit: v?.label || p.unit,
        quantity: 1,
      });
    });
  };

  if (loading) {
    return <div className="min-h-screen bg-arre-black flex items-center justify-center"><div className="font-display text-2xl text-white tracking-wider">Cargando...</div></div>;
  }
  if (!pkg) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="text-center">
          <div className="font-display text-4xl text-white tracking-wider mb-4">Paquete no encontrado</div>
          <Link to="/paquetes" className="text-arre-red hover:underline">Ver paquetes →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-arre-black min-h-screen" data-testid="package-detail-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-8">
        <Link to="/paquetes" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs tracking-widest uppercase">
          <ArrowLeft className="w-4 h-4" /> Volver a paquetes
        </Link>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
          <div className="relative bg-arre-surface aspect-[4/5] overflow-hidden border border-arre-border grain">
            <SafeImage src={pkg.image} alt={pkg.name} fallbackLabel={pkg.name} className="w-full h-full object-cover" />
            {pkg.badge && (
              <span className="absolute top-6 left-6 bg-arre-red text-white text-xs tracking-[0.3em] uppercase font-bold px-4 py-2">
                {pkg.badge}
              </span>
            )}
          </div>

          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ {pkg.tagline}</div>
            <h1 className="font-display text-6xl md:text-8xl tracking-wider uppercase text-white leading-[0.85]">
              {pkg.name}
            </h1>
            <p className="font-serif-display italic text-xl text-white/80 mt-4 leading-snug">{pkg.description}</p>

            <div className="flex items-center gap-6 mt-6 text-white/70">
              <span className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-arre-red" />
                {pkg.serves}
              </span>
              <span className="flex items-center gap-2 text-sm">
                <PackageIcon className="w-4 h-4 text-arre-red" />
                {pkg.items.length} productos
              </span>
            </div>

            <div className="mt-8 py-6 border-y border-arre-border">
              <div className="font-display text-6xl text-white" data-testid="pkg-price">{fmtMXN(pkg.price)}</div>
              <div className="text-xs tracking-widest uppercase text-white/50 mt-1">Precio total del paquete</div>
            </div>

            {/* Items list */}
            <div className="mt-8">
              <h3 className="font-display text-2xl tracking-wider uppercase text-white mb-4">Incluye:</h3>
              <ul className="space-y-2">
                {pkg.items.map((it, idx) => (
                  <li key={idx} className="flex items-center gap-3 py-2 border-b border-arre-border/60">
                    <Check className="w-4 h-4 text-arre-red flex-shrink-0" />
                    <span className="text-white flex-1">{it.name}</span>
                    <span className="text-white/50 text-sm tracking-wider">{it.weight}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={handleAddToCart}
              className="w-full btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase px-8 py-5 mt-8"
              data-testid="add-pkg-to-cart"
            >
              {combos.length > 0
                ? `Agregar combo (${combos.length + 1} items) — ${fmtMXN(grandTotal)}`
                : `Agregar paquete al carrito — ${fmtMXN(pkg.price)}`}
            </button>

            {/* COMBO SUGGESTIONS */}
            <ComboSuggestions slug={pkg.slug} kind="package" onChange={onCombosChange} />

            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { icon: Award, label: "Premium" },
                { icon: Truck, label: "Envío rápido" },
                { icon: Flame, label: "Listo parrilla" },
              ].map((t, i) => (
                <div key={i} className="flex flex-col items-center text-center p-3 border border-arre-border">
                  <t.icon className="w-5 h-5 text-arre-red mb-1" strokeWidth={1.5} />
                  <div className="font-display text-xs tracking-widest uppercase text-white">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
